/**
 * <bl-drawer> — accessible slide-over panel.
 * Focus trap, escape to close, focus restore, background inert,
 * reference-counted scroll lock, prefers-reduced-motion aware.
 *
 * Custom element (not a registry component) because openers may exist before
 * the panel is parsed; upgrade semantics are handled by the platform.
 */

const sdk = () => window.BrightLeaf;

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class BlDrawer extends HTMLElement {
  #lastFocused = null;

  connectedCallback() {
    this.panel = this.querySelector('.bl-drawer__panel');
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-bl-drawer-close]')) this.close();
    });
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    if (this.hasAttribute('open')) sdk()?.ui.unlockScroll();
  }

  get name() {
    return this.dataset.blDrawer;
  }

  open() {
    if (this.hasAttribute('open')) return;
    this.#lastFocused = document.activeElement;
    this.hidden = false;
    // Force a frame so the entry transition runs from the closed state.
    void this.offsetWidth;
    this.setAttribute('open', '');
    sdk()?.ui.lockScroll();
    this.#setBackgroundInert(true);
    window.requestAnimationFrame(() => {
      (this.querySelector('[data-bl-autofocus]') || this.querySelector(FOCUSABLE) || this.panel).focus({ preventScroll: true });
    });
    sdk()?.events.emit('bl:drawer:opened', { name: this.name });
  }

  close() {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    sdk()?.ui.unlockScroll();
    this.#setBackgroundInert(false);
    window.setTimeout(() => {
      this.hidden = true;
    }, this.#duration());
    this.#lastFocused?.focus?.({ preventScroll: true });
    sdk()?.events.emit('bl:drawer:closed', { name: this.name });
  }

  toggle() {
    if (this.hasAttribute('open')) this.close();
    else this.open();
  }

  #duration() {
    return sdk()?.utils.motionDuration(this) ?? 200;
  }

  #setBackgroundInert(state) {
    /* The drawer is rendered inside its owning section, not as a direct child
       of <body>, so skip the branch that contains it — marking that ancestor
       inert would make the drawer itself unfocusable and unclickable. */
    [...document.body.children].forEach((node) => {
      if (node === this || node.contains(this)) return;
      if (state) node.setAttribute('inert', '');
      else node.removeAttribute('inert');
    });
  }

  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const items = [...this.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
}

if (!customElements.get('bl-drawer')) customElements.define('bl-drawer', BlDrawer);

/* Global opener delegation: works for markup injected after load. */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-bl-drawer-open]');
  if (!opener) return;
  const drawer = document.querySelector(`[data-bl-drawer="${opener.dataset.blDrawerOpen}"]`);
  if (!drawer) return;
  event.preventDefault();
  drawer.open();
});

export default BlDrawer;
